import React, { useState } from 'react';
import { Search, Check, Download } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useNotificationStore } from '@/stores/notificationStore';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface UnsplashImage {
  id: string;
  urls: {
    raw: string;
    full: string;
    regular: string;
    small: string;
    thumb: string;
  };
  width: number;
  height: number;
  description: string | null;
  alt_description: string | null;
  photographer: {
    name: string;
    username: string;
    profile_url: string;
  };
  download_location: string;
}

interface UnsplashImageSearchProps {
  city: string;
  country: string;
  destinationContentId: string;
  onImagesAdded: (images: Array<{ url: string; attachment_id: string }>) => void;
  onClose: () => void;
}

export function UnsplashImageSearch({
  city,
  country,
  destinationContentId,
  onImagesAdded,
  onClose,
}: UnsplashImageSearchProps) {
  const notificationStore = useNotificationStore();
  const [searchQuery, setSearchQuery] = useState(`${city}${country ? ` ${country}` : ''}`);
  const [isSearching, setIsSearching] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [images, setImages] = useState<UnsplashImage[]>([]);
  const [selectedImageIds, setSelectedImageIds] = useState<Set<string>>(new Set());
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      notificationStore.error('Please enter a search query');
      return;
    }

    setIsSearching(true);
    setHasSearched(true);
    try {
      const params = new URLSearchParams({
        query: searchQuery.trim(),
        per_page: '12',
      });
      const response = await apiFetch(`/api/unsplash/search?${params}`);
      const data = await response.json();

      if (data?.success && data?.data?.images) {
        setImages(data.data.images);
        setSelectedImageIds(new Set()); // Clear previous selections

        if (data.data.images.length === 0) {
          notificationStore.info('No images found. Try a different search.');
        }
      }
    } catch (error: any) {
      console.error('[UnsplashImageSearch] Search failed:', error);
      notificationStore.error(error.message || 'Failed to search images', { title: 'Search Error' });
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSearching) {
      handleSearch();
    }
  };

  const toggleImageSelection = (imageId: string) => {
    setSelectedImageIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(imageId)) {
        newSet.delete(imageId);
      } else {
        newSet.add(imageId);
      }
      return newSet;
    });
  };

  const handleDownloadSelected = async () => {
    if (selectedImageIds.size === 0) {
      notificationStore.error('Please select at least one image');
      return;
    }

    setIsDownloading(true);
    try {
      const selectedImages = images
        .filter((img) => selectedImageIds.has(img.id))
        .map((img) => ({
          id: img.id,
          url: img.urls.regular, // Use regular size (1080px) for good quality
          download_location: img.download_location,
          photographer: img.photographer,
        }));

      const response = await apiFetch('/api/unsplash/download', {
        method: 'POST',
        body: JSON.stringify({
          images: selectedImages,
          destination_content_id: destinationContentId,
        }),
      });
      const data = await response.json();

      if (data?.success && data?.data?.images) {
        const uploadedImages = data.data.images.map((img: any) => ({
          url: img.url,
          attachment_id: img.attachment_id,
        }));

        notificationStore.success(data.data.message || 'Images added successfully', { title: 'Success' });
        onImagesAdded(uploadedImages);
        onClose();
      }
    } catch (error: any) {
      console.error('[UnsplashImageSearch] Download failed:', error);
      notificationStore.error(error.message || 'Failed to download images', { title: 'Download Error' });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Search Unsplash Images"
      description={`Find high-quality images for ${city}${country ? `, ${country}` : ''}`}
      size="xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isDownloading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleDownloadSelected}
            disabled={selectedImageIds.size === 0 || isDownloading}
            isLoading={isDownloading}
            leftIcon={!isDownloading ? <Download className="w-4 h-4" /> : undefined}
          >
            {isDownloading ? 'Downloading...' : `Add Selected (${selectedImageIds.size})`}
          </Button>
        </>
      }
    >
      {/* Search Input */}
      <div className="mb-6 flex gap-3">
        <div className="flex-1">
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter search query..."
            leftIcon={<Search className="w-4 h-4" />}
          />
        </div>
        <Button
          variant="secondary"
          onClick={handleSearch}
          disabled={isSearching || !searchQuery.trim()}
          isLoading={isSearching}
          leftIcon={!isSearching ? <Search className="w-4 h-4" /> : undefined}
        >
          {isSearching ? 'Searching...' : 'Search'}
        </Button>
      </div>

      {/* Results Grid */}
      <div className="min-h-[300px] max-h-[50vh] overflow-y-auto">
        {!hasSearched && (
          <div className="flex flex-col items-center justify-center h-[300px] text-muted-foreground">
            <Search className="w-16 h-16 mb-4 opacity-50" />
            <p className="text-lg">Enter a search query and click Search</p>
            <p className="text-sm mt-2">or press Enter to search</p>
          </div>
        )}

        {hasSearched && images.length === 0 && !isSearching && (
          <div className="flex flex-col items-center justify-center h-[300px] text-muted-foreground">
            <Search className="w-16 h-16 mb-4 opacity-50" />
            <p className="text-lg">No images found</p>
            <p className="text-sm mt-2">Try a different search query</p>
          </div>
        )}

        {images.length > 0 && (
          <>
            <div className="mb-4 text-sm text-muted-foreground flex items-center justify-between">
              <span>Found {images.length} images</span>
              {selectedImageIds.size > 0 ? (
                <span className="font-medium text-primary-600">
                  {selectedImageIds.size} image{selectedImageIds.size !== 1 ? 's' : ''} selected
                </span>
              ) : (
                <span>Click on images to select them</span>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map((image) => {
                const isSelected = selectedImageIds.has(image.id);

                return (
                  <button
                    key={image.id}
                    onClick={() => toggleImageSelection(image.id)}
                    className={`relative group cursor-pointer rounded-lg overflow-hidden transition-all ${
                      isSelected
                        ? 'ring-4 ring-primary-500 ring-offset-2'
                        : 'hover:ring-2 hover:ring-gray-300'
                    }`}
                  >
                    <div className="aspect-square">
                      <img
                        src={image.urls.small}
                        alt={image.alt_description || image.description || 'Unsplash photo'}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Selection Indicator */}
                    <div
                      className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                        isSelected
                          ? 'bg-primary-500 text-white scale-100'
                          : 'bg-white/80 text-gray-400 scale-0 group-hover:scale-100'
                      }`}
                    >
                      <Check className="w-4 h-4" />
                    </div>

                    {/* Photographer Info */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                      <p className="text-xs text-white truncate">
                        by {image.photographer.name}
                      </p>
                    </div>

                    {/* Overlay on Hover */}
                    {!isSelected && (
                      <div className="absolute inset-0 bg-primary-500/0 group-hover:bg-primary-500/10 transition-colors" />
                    )}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Unsplash Attribution */}
      <div className="mt-6 pt-4 border-t text-xs text-muted-foreground text-center">
        Images powered by{' '}
        <a
          href="https://unsplash.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-600 hover:underline"
        >
          Unsplash
        </a>
      </div>
    </Modal>
  );
}
